import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    Heat,
    Player,
    PlayerRegistration,
    PlayerTournamentDetail,
    Tournament,
    EmailConfig,
    formConfig,
    TournamentForm,
    TournamentConfig,
    TournamentResourceType,
    TournamentMember,
} from '@iziping/common';
import { apiClient } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import type { ApiError } from '@iziping/common';

export const useGetTournaments = () => {
    return useQuery<Tournament[], ApiError>({
        queryKey: [QUERY_CACHE.TOURNAMENTS],
        queryFn: async () => {
            const response = await apiClient.get<Tournament[]>(`${ENDPOINTS.tournaments}`);
            return response.data;
        },
        enabled: true,
    });
};

export const useGetTournament = (id?: number) => {
    return useQuery<Tournament>({
        queryKey: [QUERY_CACHE.TOURNAMENT],
        queryFn: async () => {
            if (!id) throw new Error('ID manquant');
            const response = await apiClient.get<Tournament>(ENDPOINTS.tournament(id));
            return response.data;
        },
        enabled: !!id,
    });
};

export const useSaveTournament = () => {
    return useMutation<Tournament, ApiError, { id?: number; data: TournamentForm }>({
        mutationFn: async ({ id, data }) => {
            if (id) {
                // update
                const response = await apiClient.put<Tournament>(`${ENDPOINTS.tournament(id)}`, data);
                return response.data;
            } else {
                // create
                const response = await apiClient.post<Tournament>(ENDPOINTS.tournaments, data);
                return response.data;
            }
        },
    });
};

export const useGetTournamentRegistrations = (id: number) => {
    return useQuery<Player[]>({
        queryKey: [QUERY_CACHE.TOURNAMENT_REGISTRATIONS, id],
        queryFn: async () => {
            const response = await apiClient.get<Player[]>(ENDPOINTS.tournamentRegistrations(id));
            return response.data;
        },
        enabled: true,
    });
};

interface CreateTournamentRegistrationProps {
    tournamentId: number;
    playerId: string;
}

export const useCreateTournamentRegistration = () => {
    return useMutation<PlayerRegistration, ApiError, any>({
        mutationKey: [QUERY_CACHE.CREATE_TOURNAMENT_REGISTRATION],
        mutationFn: async (params: CreateTournamentRegistrationProps) => {
            const response = await apiClient.post<PlayerRegistration>(
                ENDPOINTS.tournamentRegisterPlayer(params.tournamentId, params.playerId)
            );
            return response.data;
        },
    });
};

interface RemoveTournamentRegistrationParams {
    id: number;
    playerId: number;
}

export const useRemoveTournamentRegistration = () => {
    return useMutation<void, ApiError, any>({
        mutationKey: [QUERY_CACHE.REMOVE_HEAT_REGISTRATION],
        mutationFn: async (params: RemoveTournamentRegistrationParams) => {
            const response = await apiClient.delete(ENDPOINTS.removeTournamentRegistration(params.id, params.playerId));
            return response.data;
        },
    });
};

interface TournamentViewToken {
    token: string;
    expiresAt: string;
}

export const useGetTournamentViewToken = (id: number) => {
    return useQuery<TournamentViewToken>({
        queryKey: ['TOURNAMENT_VIEW_TOKEN', id],
        queryFn: async () => {
            const response = await apiClient.get<TournamentViewToken>(ENDPOINTS.tournamentViewToken(id));
            return response.data;
        },
        enabled: false,
    });
};

export const useRevokeViewToken = () => {
    return useMutation<void, ApiError, number>({
        mutationFn: async (id: number) => {
            await apiClient.delete(ENDPOINTS.revokeViewToken(id));
        },
    });
};

export const useGetPlayerSummary = (id: number, playerId: string) => {
    return useQuery<PlayerTournamentDetail>({
        queryKey: [QUERY_CACHE.PLAYER_SUMMARY, id],
        queryFn: async () => {
            const response = await apiClient.get<PlayerTournamentDetail>(ENDPOINTS.playerSummary(id, playerId));
            return response.data;
        },
        enabled: true,
    });
};

interface HeatsToRegisterParams {
    playerId: string;
    tournamentId: number;
}

export const useGetHeatsToRegister = (params: HeatsToRegisterParams) => {
    return useQuery<Heat[], ApiError>({
        queryKey: [QUERY_CACHE.HEATS_TO_REGISTER, params.tournamentId, params.playerId],
        queryFn: async () => {
            const response = await apiClient.get<Heat[]>(ENDPOINTS.heatsToRegister(params.tournamentId), {
                params: { playerId: params.playerId },
            });
            return response.data;
        },
        enabled: true,
    });
};

export const useGetTournamentConfig = (tournamentId: number) => {
    return useQuery<TournamentConfig, ApiError>({
        queryKey: [QUERY_CACHE.TOURNAMENT_CONFIG, tournamentId],
        queryFn: async () => {
            const response = await apiClient.get<TournamentConfig>(ENDPOINTS.tournamentConfig(tournamentId));
            return response.data;
        },
        enabled: !!tournamentId,
    });
};

export const useSaveEmailConfig = (tournamentId: number) => {
    return useMutation<EmailConfig, ApiError, EmailConfig>({
        mutationFn: async (data) => {
            const response = await apiClient.put<EmailConfig>(ENDPOINTS.tournamentEmailConfig(tournamentId), data);
            return response.data;
        },
    });
};

export const useGetEmailPreview = (tournamentId: number) => {
    return useMutation<string, ApiError, string>({
        mutationFn: async (lang) => {
            const response = await apiClient.get<string>(ENDPOINTS.tournamentEmailPreview(tournamentId, lang), {
                responseType: 'text',
                headers: { Accept: 'text/html' },
            });
            return response.data;
        },
    });
};

export const useSendPreviewEmail = (tournamentId: number) => {
    return useMutation<void, ApiError, string>({
        mutationFn: async (lang) => {
            await apiClient.post(ENDPOINTS.tournamentEmailPreviewSend(tournamentId, lang));
        },
    });
};

export const useDeleteEmailConfig = (tournamentId: number) => {
    return useMutation<void, ApiError>({
        mutationFn: async () => {
            await apiClient.delete(ENDPOINTS.tournamentEmailConfig(tournamentId));
        },
    });
};

export const useSendEmailsToPlayers = (tournamentId: number) => {
    return useMutation<void, ApiError, void>({
        mutationFn: async () => {
            await apiClient.post(ENDPOINTS.sendEmailsToPlayers(tournamentId));
        },
    });
};

export const useGetPreRegistrationConfig = (tournamentId: number) => {
    return useQuery<{ preRegistrationEnabled: boolean; preRegistrationConfig?: formConfig }, ApiError>({
        queryKey: [QUERY_CACHE.TOURNAMENT_PRE_REGISTRATION_CONFIG, tournamentId],
        queryFn: async () => {
            const response = await apiClient.get<TournamentConfig>(ENDPOINTS.tournamentConfig(tournamentId));
            return {
                preRegistrationEnabled: response.data.preRegistrationEnabled,
                preRegistrationConfig: response.data.preRegistrationConfig,
            };
        },
        enabled: !!tournamentId,
    });
};

export const useSavePreRegistrationConfig = (tournamentId: number) => {
    return useMutation<TournamentConfig, ApiError, { showStats: boolean }>({
        mutationKey: [QUERY_CACHE.SAVE_PRE_REGISTRATION_CONFIG],
        mutationFn: async (data) => {
            const response = await apiClient.put<TournamentConfig>(ENDPOINTS.preRegistrationConfig(tournamentId), data);
            return response.data;
        },
    });
};

export const useDeletePreRegistrationConfig = (tournamentId: number) => {
    const queryClient = useQueryClient();
    return useMutation<TournamentConfig, ApiError, void>({
        mutationKey: [QUERY_CACHE.DELETE_PRE_REGISTRATION_CONFIG],
        mutationFn: async () => {
            const response = await apiClient.delete<TournamentConfig>(ENDPOINTS.preRegistrationConfig(tournamentId));
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_CACHE.TOURNAMENT_PRE_REGISTRATION_CONFIG, tournamentId],
            });
        },
    });
};

export const useUploadTournamentResource = (tournamentId: number) => {
    return useMutation<{ url: string }, ApiError, { type: TournamentResourceType; file: File }>({
        mutationKey: [QUERY_CACHE.UPLOAD_TOURNAMENT_RESOURCE],
        mutationFn: async ({ type, file }) => {
            const formData = new FormData();
            formData.append('file', file);
            const response = await apiClient.post<{ url: string }>(
                ENDPOINTS.tournamentResource(tournamentId, type),
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return response.data;
        },
    });
};

export const useDeleteTournamentResource = (tournamentId: number) => {
    return useMutation<void, ApiError, { type: TournamentResourceType }>({
        mutationKey: [QUERY_CACHE.DELETE_TOURNAMENT_RESOURCE],
        mutationFn: async ({ type }) => {
            await apiClient.delete(ENDPOINTS.tournamentResource(tournamentId, type));
        },
    });
};

export const useGetTournamentResourceUrl = (tournamentId: number) => {
    return useMutation<{ url: string }, ApiError, { type: TournamentResourceType }>({
        mutationFn: async ({ type }) => {
            const response = await apiClient.get<{ url: string }>(ENDPOINTS.tournamentResource(tournamentId, type));
            return response.data;
        },
    });
};

export const useGetTournamentMembers = (tournamentId: number) => {
    return useQuery<TournamentMember[], ApiError>({
        queryKey: [QUERY_CACHE.TOURNAMENT_MEMBERS, tournamentId],
        queryFn: async () => {
            const response = await apiClient.get<TournamentMember[]>(ENDPOINTS.tournamentMembers(tournamentId));
            return response.data;
        },
        enabled: !!tournamentId,
    });
};

export const useInviteTournamentMember = (tournamentId: number) => {
    return useMutation<TournamentMember, ApiError, { login: string }>({
        mutationFn: async ({ login }) => {
            const response = await apiClient.post<TournamentMember>(ENDPOINTS.tournamentMembers(tournamentId), {
                login,
            });
            return response.data;
        },
    });
};

export const useRemoveTournamentMember = (tournamentId: number) => {
    return useMutation<void, ApiError, { userId: number }>({
        mutationFn: async ({ userId }) => {
            await apiClient.delete(ENDPOINTS.tournamentMember(tournamentId, userId));
        },
    });
};
